import { useLayoutEffect, useRef } from "react";
import { useIsMobile } from "hooks/useIsMobile";

enum RelativeScrollPosition {
    BEFORE,
    INSIDE,
    AFTER,
}

export interface UseScrollableBlockProps {
    stopOffset?: number;
}

// flag for syncing cleanup of global styles on reset
let hasCleanedGlobalStyles = false;

const getCurrentPosition = (
    stopScrollThreshold: number,
    scrollLength: number
) => {
    if (window.scrollY > stopScrollThreshold + scrollLength) {
        return RelativeScrollPosition.AFTER;
    } else if (window.scrollY > stopScrollThreshold) {
        return RelativeScrollPosition.INSIDE;
    } else {
        return RelativeScrollPosition.BEFORE;
    }
};

export const addedScrollLengthProperty = "addedScrollLength";

export const useScrollableBlock = ({
    stopOffset = 0,
}: UseScrollableBlockProps = {}) => {
    const containerRef = useRef<HTMLElement>(null);
    // save offset in a reference for the handler to have access to the up-to-date
    // value without rerunning effect and recreating it
    const offsetRef = useRef(stopOffset);
    offsetRef.current = stopOffset;
    const isMobile = useIsMobile();

    useLayoutEffect(() => {
        const container = containerRef.current;

        if (!container) {
            return;
        }

        // determines scroll direction depending on where the content overflows
        const getScrollDirection = () =>
            container.scrollWidth > container.clientWidth
                ? "horizontal"
                : "vertical";
        // returns length of the overflowing part of container's contents
        const getScrollLength = (element: HTMLElement) =>
            getScrollDirection() === "horizontal"
                ? element.scrollWidth - element.clientWidth
                : element.scrollHeight - element.clientHeight;

        // retrieve additional height added by other containers appearing earlier
        // on the page up to this point
        const totalAddedHeight = Number(document.body.dataset.addedHeight) || 0;
        const scrollLength = getScrollLength(container);

        const newAddedHeight = totalAddedHeight + scrollLength;

        document.body.style.cssText += `position: absolute;top: 0;left: 0;width: 100%;top: 0px;`;
        // add container's inner scroll length to the page height
        document.documentElement.style.height =
            document.body.scrollHeight + newAddedHeight + "px";
        // store added height
        document.body.dataset.addedHeight = newAddedHeight.toString();
        container.dataset[addedScrollLengthProperty] = scrollLength.toString();

        let lastScrollPosition = RelativeScrollPosition.BEFORE;
        let lastAddedScroll = 0;

        const scrollHandler = () => {
            const totalAddedScroll =
                Number(document.body.dataset.addedScroll) || 0;
            const offsetTop = Math.max(
                container.offsetTop - offsetRef.current,
                0
            );
            const stopScrollThreshold =
                totalAddedScroll - lastAddedScroll + offsetTop;
            const scrollDirection = getScrollDirection();
            const scrollLength = getScrollLength(container);

            // retrieve current scroll position relative to container's position
            const currentScrollPosition = getCurrentPosition(
                stopScrollThreshold,
                scrollLength
            );

            // calculate how much to move container's inner scroll
            const innerScrollOffset = Math.min(
                Math.max(window.scrollY - stopScrollThreshold, 0),
                scrollLength
            );

            // update container's inner scroll
            if (scrollDirection === "horizontal") {
                container.scrollLeft = innerScrollOffset;
            } else {
                container.scrollTop = innerScrollOffset;
            }

            if (
                currentScrollPosition !== lastScrollPosition ||
                currentScrollPosition === RelativeScrollPosition.INSIDE
            ) {
                const newTotalAddedScroll =
                    totalAddedScroll - lastAddedScroll + innerScrollOffset;
                document.body.dataset.addedScroll =
                    newTotalAddedScroll.toString();
                lastAddedScroll = innerScrollOffset;

                if (currentScrollPosition === RelativeScrollPosition.INSIDE) {
                    document.body.style.cssText += `position: fixed;top: 0;left: 0;width: 100%;top: -${offsetTop}px;`;
                } else {
                    document.body.style.cssText += `position: absolute;top: 0;left: 0;width: 100%;top: ${newTotalAddedScroll}px;`;
                }
            }

            lastScrollPosition = currentScrollPosition;
        };
        window.addEventListener("scroll", scrollHandler);

        // cleanup function
        return () => {
            window.removeEventListener("scroll", scrollHandler);

            // clean global styles only once for all hook instances for a cleanup
            // cycle triggered by the same update
            if (!hasCleanedGlobalStyles) {
                hasCleanedGlobalStyles = true;
                document.body.dataset.addedScroll = "0";
                document.body.dataset.addedHeight = "0";
                document.documentElement.style.height = "";
                document.body.style.cssText += `position: absolute;top: 0;left: 0;width: 100%;top: 0px;`;
                window.scrollTo(0, 0);

                // reset cleanup flag asynchronously to ensure this happens only
                // after effect's cleanup functions in other instances of the
                // hook has run
                setTimeout(() => {
                    hasCleanedGlobalStyles = false;
                });
            }
        };
    }, [isMobile]);

    return { ref: containerRef };
};
